import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, MessageCircle, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { ASSETS, WA_DEFAULT_MSG, waLink } from "@/data/site";
import api from "@/lib/api";

const LINKS = [
  { name: "Home", path: "/" },
  { name: "Projects", path: "/projects" },
  { name: "Expertise", path: "/expertise" },
  { name: "Collections", path: "/collections" },
  { name: "Heritage", path: "/#heritage" },
  { name: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const handleWhatsApp = () => {
    api.post("/enquiries", { context: "Navbar WhatsApp", page: pathname }).catch(() => {});
    window.open(waLink(WA_DEFAULT_MSG), "_blank", "noopener");
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled || open
          ? "border-b border-gold/15 bg-obsidian/85 backdrop-blur-md"
          : "bg-gradient-to-b from-obsidian/70 to-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 sm:px-12 lg:px-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img
            src={ASSETS.logo}
            alt="ShilpStones Creations logo"
            className="h-11 w-11 rounded-full ring-1 ring-gold/60 object-cover"
          />
          <span className="leading-tight">
            <span className="block font-serif text-lg font-semibold tracking-wide text-stone-100">
              ShilpStones
            </span>
            <span className="block font-display text-[10px] tracking-[0.4em] text-gold-soft">
              CREATIONS
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
          {LINKS.map((l) =>
            l.path.includes("#") ? (
              <Link
                key={l.name}
                to={l.path}
                data-testid={`nav-link-${l.name.toLowerCase()}`}
                className="text-xs font-medium uppercase tracking-[0.2em] text-stone-300 transition-colors duration-300 hover:text-gold"
              >
                {l.name}
              </Link>
            ) : (
              <NavLink
                key={l.name}
                to={l.path}
                data-testid={`nav-link-${l.name.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-xs font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-stone-300 hover:text-gold"
                  }`
                }
              >
                {l.name}
              </NavLink>
            )
          )}
          <button
            onClick={handleWhatsApp}
            data-testid="nav-whatsapp-button"
            className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_25px_rgba(212,175,55,0.4)]"
          >
            <MessageCircle className="h-4 w-4" />
            Enquire
          </button>
        </nav>

        <button
          className="text-stone-200 lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-gold/10 bg-obsidian/95 backdrop-blur-md lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l) => (
                <Link
                  key={l.name}
                  to={l.path}
                  data-testid={`nav-mobile-link-${l.name.toLowerCase()}`}
                  className="py-3 font-serif text-2xl text-stone-200 transition-colors hover:text-gold"
                >
                  {l.name}
                </Link>
              ))}
              <button
                onClick={handleWhatsApp}
                data-testid="nav-mobile-whatsapp"
                className="mt-4 flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-obsidian"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp Enquiry
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
